import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, KeyRound, LogOut, Settings } from "lucide-react";
import useAuthStore from "@/store/authStore";
import { useShallow } from "zustand/react/shallow";
import AgencySwitcher from "./AgencySwitcher";
import NotificationBell from "./NotificationBell";

interface HeaderProps {
  onMenuClick: () => void;
  onLogout: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, onLogout }) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { user, user_role } = useAuthStore(
    useShallow((state) => ({
      user: state.user,
      user_role: state.user_role,
    })),
  );

  const userInitial = user?.first_name?.[0]?.toUpperCase() || "U";
  const fullName = user ? `${user.first_name} ${user.last_name}` : "User";

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  return (
    <nav className="h-14 bg-gradient-to-r from-[#EA33C0] to-[#D020A8] flex items-center px-4 shadow-md z-20">
      {/* Left: Hamburger (mobile) + Branding */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          aria-label="Open sidebar"
          className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white/10 transition-colors"
        >
          <svg
            className="h-6 w-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
        <h1
          onClick={() => navigate("/dashboard")}
          className="text-white text-lg font-bold tracking-wide cursor-pointer select-none"
        >
          MediaBridge
        </h1>
      </div>

      {/* Right: Notification + User dropdown */}
      <div className="flex-1 flex justify-end items-center gap-2">
        {/* Notification bell: latest notifications + unread badge */}
        <NotificationBell />

        {/* User avatar dropdown */}
        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setDropdownOpen((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={dropdownOpen}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white/60 flex items-center justify-center overflow-hidden">
              {user?.profile_picture_url_signed ? (
                <img
                  src={user.profile_picture_url_signed}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm font-semibold text-white">
                  {userInitial}
                </span>
              )}
            </div>
            <span className="hidden md:block text-white text-sm font-medium max-w-[120px] truncate">
              {fullName}
            </span>
            <svg
              className={`w-4 h-4 text-white/80 transition-transform ${
                dropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Dropdown menu */}
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
              {/* User info header */}
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {fullName}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {user_role || "N/A"}
                </p>
              </div>

              {/* Agency Switcher */}
              <div>
                <AgencySwitcher />
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate("/profile");
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-pink-50 hover:text-[#EA33C0] transition-colors"
                >
                  <User className="w-4 h-4" />
                  Profile
                </button>
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate("/profile");
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-pink-50 hover:text-[#EA33C0] transition-colors"
                >
                  <KeyRound className="w-4 h-4" />
                  Change Password
                </button>
              </div>

              {/* Account Settings — agency-scoped, not for Global Admin */}
              {user_role !== "Global Admin" && (
                <div className="border-t border-gray-100 py-1">
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      navigate("/account");
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-pink-50 hover:text-[#EA33C0] transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Account Settings
                  </button>
                </div>
              )}

              {/* Logout */}
              <div className="border-t border-gray-100 py-1">
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    onLogout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Header;
